// @flow

import type { Dispatch } from 'redux';
import { fetchData, flattenError, isMultiLanguage } from '../../helpers';
import { getActiveLanguage } from '../activeLanguage';
import { GET_SETTINGS, GET_SETTINGS_SUCCESS, GET_SETTINGS_FAILED } from './constants';
import {
  isFetching,
  isFetched,
} from './selectors';
import { receivedCompareSettings } from '../compareSettings';
import { receivedComponentSettings } from '../componentSettings';
import { receivedConfiguratorSettings } from '../configuratorSettings';
import { receivedFormSettings } from '../formSettings';
import { receivedContactSettings } from '../contactSettings';
import { receivedFooterSettings } from '../footerSettings';
import { receivedFormidableSettings } from '../formidableSettings';
import { receivedMenus } from '../menus';
import { receivedSiteSettings } from '../siteSettings';
import { receivedSocialSettings } from '../socialSettings';
import { receivedWpSettings } from '../wpSettings';
import { receivedAssortmentSettings } from '../assortmentSettings';
import { receivedNewsSettings } from '../newsSettings';
import { receivedBlogSettings } from '../blogSettings';
import { receivedBusinessLabelSettings } from '../businessLabelSettings';
import { receivedProductTypeSettings } from '../productTypeSettings';
import type { Arguments, FetchError } from '../../helpers/fetchData';
import type { FlatError } from '../../helpers/flattenError';
import type {
  Settings,
} from '../../types';

export type StartFetchSettings = {|
  type: typeof GET_SETTINGS,
|};
export const startFetchSettings = (): StartFetchSettings => ({
  type: GET_SETTINGS,
});

export type ReceivedSettings = {|
  type: typeof GET_SETTINGS_SUCCESS,
  payload: Settings,
|};
export const receivedSettings = (data: Settings): ReceivedSettings => ({
  type: GET_SETTINGS_SUCCESS,
  payload: data,
});

export type FailedSettings = {|
  type: typeof GET_SETTINGS_FAILED,
  payload: FlatError,
|};
export const failedSettings = (err: Error | FetchError): FailedSettings => ({
  type: GET_SETTINGS_FAILED,
  payload: flattenError(err),
});

export const dispatchReceivedSettings = (settings: Settings) => (dispatch: Dispatch<any>) => {
  const {
    component_settings,
    posttype_settings,
    contact_settings,
    footer_settings,
    formidable_settings,
    site_settings,
    social_settings,
    wp_settings,
    template_settings,
    menus,
    business_label_settings,
    product_type_settings,
  } = settings;

  if (posttype_settings) {
    const { configurator, form } = posttype_settings;
    if (configurator) {
      dispatch(receivedConfiguratorSettings(configurator));
    }
    if (form) {
      // $FlowFixMe
      dispatch(receivedFormSettings(settings.template_settings && settings.template_settings.form));
    }
  }

  if (template_settings && template_settings.assortment) {
    dispatch(receivedAssortmentSettings(template_settings.assortment));
  }

  if (template_settings && template_settings.news) {
    dispatch(receivedNewsSettings(template_settings.news));
  }

  if (template_settings && template_settings.blog) {
    dispatch(receivedBlogSettings(template_settings.blog));
  }

  if (template_settings && template_settings.compare) {
    dispatch(receivedCompareSettings(template_settings.compare));
  }

  if (contact_settings) {
    dispatch(receivedContactSettings(contact_settings));
  }

  if (footer_settings) {
    dispatch(receivedFooterSettings(footer_settings));
  }

  if (formidable_settings) {
    dispatch(receivedFormidableSettings(formidable_settings));
  }

  if (site_settings) {
    dispatch(receivedSiteSettings(site_settings));
  }

  if (social_settings) {
    dispatch(receivedSocialSettings(social_settings));
  }

  if (wp_settings) {
    dispatch(receivedWpSettings(wp_settings));
  }

  if (menus) {
    dispatch(receivedMenus(menus));
  }

  if (component_settings) {
    dispatch(receivedComponentSettings(component_settings));
  }

  if (business_label_settings) {
    dispatch(receivedBusinessLabelSettings(business_label_settings));
  }

  if (product_type_settings) {
    dispatch(receivedProductTypeSettings(product_type_settings));
  }
};

type Fetcher = (_: Arguments) => Promise<{ json: Settings }>;
// SSR will run this function with fetchSettings => fetchDataWithAuth
export default (fn: Fetcher = fetchData) => async (dispatch: Function, getState: Function) => {
  const state = getState();
  // dont double fetch it
  if (isFetching(state)) {
    return true;
  }
  // we dont need to load it when we have it
  if (isFetched(state)) {
    return true;
  }

  dispatch(startFetchSettings());
  const language = isMultiLanguage() ? getActiveLanguage(state) : null;

  let settings: Settings;
  try {
    const { json } = await fn({
      endpoint: '/api/settings',
      language,
      uri: '',
    });
    settings = json;
  } catch (err) {
    return dispatch(failedSettings(err));
  }

  dispatch(dispatchReceivedSettings(settings));

  return dispatch(receivedSettings(settings));
};
